// PPC Farm Mail Tracker Service
// This service handles data operations for the PPC Farm Mail Tracker app
// Phase 3: Multi-ledger support with generic data operations

import {
  getFirestore,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  where,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  QueryConstraint,
  getCountFromServer,
  doc,
  getDoc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  PpcFarmTargetLedgerData,
  PpcFarmCohortLedgerData,
  PpcFarmMailingLedgerData,
  PpcFarmStatistics,
  PpcFarmFilters,
  PpcFarmSortOptions,
  PpcFarmPaginationOptions,
  DashboardMetricsEnhanced,
  EnhancedDashboardMetrics,
  KeepaPipelineStatus,
  PPC_FARM_COLLECTIONS,
  PPC_FARM_DOCUMENTS,
} from "../types/ppcFarmTypes";
import { PpcFarmAnalyticsService } from "./ppcFarmAnalyticsService";

// Generic ledger data type
export type PpcFarmLedgerData =
  | PpcFarmTargetLedgerData
  | PpcFarmCohortLedgerData
  | PpcFarmMailingLedgerData;

// Ledger type enum
export enum LedgerType {
  TARGET = "target",
  COHORT = "cohort",
  MAILING = "mailing",
}

// Interface for Target Ledger app hub metrics
export interface TargetLedgerMetrics {
  totalSellers: number;
  totalCohorts: number;
  lastSync: string | null;
}

const db = getFirestore();

// Collection names
const TARGET_LEDGER_COLLECTION = "ppcFarm_targetLedger";
const COHORT_LEDGER_COLLECTION = "ppcFarm_cohortLedger";
const MAILING_LEDGER_COLLECTION = "ppcFarm_mailingLedger";

// Legacy collection name constant for backward compatibility
const COLLECTION_NAME = TARGET_LEDGER_COLLECTION;

// Default pagination settings
const DEFAULT_PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 100;

export const ppcFarmService = {
  /**
   * Get Target Ledger data with server-side filtering, sorting, and pagination
   * This is the main method that should be used for all data fetching
   */
  getTargetLedgerData: async (options?: {
    filters?: PpcFarmFilters;
    sort?: PpcFarmSortOptions;
    pagination?: PpcFarmPaginationOptions;
    lastDoc?: QueryDocumentSnapshot<DocumentData>;
  }): Promise<{
    data: PpcFarmTargetLedgerData[];
    lastDoc?: QueryDocumentSnapshot<DocumentData>;
    hasMore: boolean;
    totalCount: number;
  }> => {
    try {
      const {
        filters = { search: "", cohort: "", addedBy: "" },
        sort = { field: "dateAdded", direction: "desc" },
        pagination = { page: 1, limit: DEFAULT_PAGE_SIZE, total: 0 },
        lastDoc,
      } = options || {};

      // Ensure page size doesn't exceed maximum
      const pageSize = Math.min(pagination.limit, MAX_PAGE_SIZE);

      console.log(`📊 Server-side query with:`, {
        filters,
        sort,
        pageSize,
        page: pagination.page,
      });

      // Build query constraints
      const constraints: QueryConstraint[] = [];

      // Apply server-side filters
      if (filters.cohort && filters.cohort !== "") {
        constraints.push(where("cohort", "==", filters.cohort));
      }
      if (filters.addedBy && filters.addedBy !== "") {
        constraints.push(where("addedBy", "==", filters.addedBy));
      }

      // ENHANCED SEARCH: Search across both seller name and seller address
      if (filters.search && filters.search.trim() !== "") {
        const searchTerm = filters.search.toLowerCase().trim();

        // For now, search by seller name (Firestore limitation - can't OR across fields)
        // TODO: Implement composite search when Firestore supports it or use client-side filtering
        constraints.push(where("sellerName_lowercase", ">=", searchTerm));
        constraints.push(
          where("sellerName_lowercase", "<=", searchTerm + "\uf8ff"),
        );
      }

      // ENHANCED SORTING: Apply default sorting with tie-breaker
      if (!filters.search || filters.search.trim() === "") {
        // Primary sort field
        constraints.push(orderBy(sort.field, sort.direction));
        // Tie-breaker: always add createdAt as secondary sort for consistent ordering
        if (sort.field !== "createdAt") {
          constraints.push(orderBy("createdAt", "desc"));
        }
      } else {
        // When searching, order by the search field to support range queries
        constraints.push(orderBy("sellerName_lowercase", "asc"));
        // Add tie-breaker for search results too
        constraints.push(orderBy("createdAt", "desc"));
      }

      // Apply pagination cursor
      if (lastDoc) {
        constraints.push(startAfter(lastDoc));
      }

      // Apply limit (get one extra to check if there are more)
      constraints.push(limit(pageSize + 1));

      // Build queries for parallel execution
      const dataQuery = query(
        collection(db, TARGET_LEDGER_COLLECTION),
        ...constraints,
      );

      // PERFORMANCE FIX: Run data query and count query in parallel
      const [querySnapshot, totalCount] = await Promise.all([
        getDocs(dataQuery),
        ppcFarmService.getTotalCount(filters),
      ]);

      const docs = querySnapshot.docs;
      const hasMore = docs.length > pageSize;

      // Remove the extra document if it exists
      if (hasMore) {
        docs.pop();
      }

      let data: PpcFarmTargetLedgerData[] = docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as PpcFarmTargetLedgerData,
      );

      // REMOVED: Client-side search filter - now handled server-side
      // All search logic is now performed by Firestore queries above

      const newLastDoc = docs.length > 0 ? docs[docs.length - 1] : undefined;

      return {
        data,
        lastDoc: newLastDoc,
        hasMore,
        totalCount,
      };
    } catch (error) {
      console.error("❌ Error fetching Target Ledger data:", error);
      throw new Error(
        `Failed to fetch Target Ledger data: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  },

  /**
   * Get total count of documents matching the current filters
   * CRITICAL FIX: Now includes server-side search in count calculation
   */
  getTotalCount: async (
    filters: PpcFarmFilters = { search: "", cohort: "", addedBy: "" },
  ): Promise<number> => {
    try {
      // Build query constraints for count (now including server-side search)
      const constraints: QueryConstraint[] = [];

      if (filters.cohort && filters.cohort !== "") {
        constraints.push(where("cohort", "==", filters.cohort));
      }
      if (filters.addedBy && filters.addedBy !== "") {
        constraints.push(where("addedBy", "==", filters.addedBy));
      }

      // ENHANCED SEARCH: Include server-side case-insensitive search in count calculation
      if (filters.search && filters.search.trim() !== "") {
        const searchTerm = filters.search.toLowerCase().trim();

        // Apply same search logic as main query - case-insensitive prefix matching
        constraints.push(where("sellerName_lowercase", ">=", searchTerm));
        constraints.push(
          where("sellerName_lowercase", "<=", searchTerm + "\uf8ff"),
        );
      }

      let totalCount = 0;

      if (constraints.length > 0) {
        const q = query(
          collection(db, TARGET_LEDGER_COLLECTION),
          ...constraints,
        );
        const snapshot = await getCountFromServer(q);
        totalCount = snapshot.data().count;

        // Server-side count is handled by Firestore queries above
      } else {
        // No constraints, get total count
        const q = query(collection(db, TARGET_LEDGER_COLLECTION));
        const snapshot = await getCountFromServer(q);
        totalCount = snapshot.data().count;
      }

      return totalCount;
    } catch (error) {
      console.error("❌ Error getting total count:", error);
      // Fallback to 0 if count fails
      return 0;
    }
  },

  /**
   * DEPRECATED: This method loads all data and should not be used
   * Kept for backward compatibility but will be removed
   */
  getAllTargetLedgerData: async (): Promise<PpcFarmTargetLedgerData[]> => {
    console.warn(
      "⚠️ getAllTargetLedgerData is deprecated and causes performance issues. Use getTargetLedgerData instead.",
    );

    try {
      // For backward compatibility, return first page of data
      const result = await ppcFarmService.getTargetLedgerData({
        pagination: { page: 1, limit: DEFAULT_PAGE_SIZE, total: 0 },
      });

      return result.data;
    } catch (error) {
      console.error("❌ Error in deprecated getAllTargetLedgerData:", error);
      return [];
    }
  },

  /**
   * Subscribe to Target Ledger data changes with PAGINATED filtering
   * Uses the same QueryConstraint[] pattern as getTargetLedgerData for consistency
   */
  subscribeTargetLedgerData: (
    callback: (data: PpcFarmTargetLedgerData[]) => void,
    options?: {
      filters?: PpcFarmFilters;
      sort?: PpcFarmSortOptions;
      limit?: number;
      lastDoc?: QueryDocumentSnapshot<DocumentData>;
    },
  ): (() => void) => {
    const {
      filters = { search: "", cohort: "", addedBy: "" },
      sort = { field: "dateAdded", direction: "desc" },
      limit: limitCount = 50,
      lastDoc,
    } = options || {};

    // Build query constraints using the same pattern as getTargetLedgerData
    const constraints: QueryConstraint[] = [];

    // Apply server-side filters
    if (filters.cohort && filters.cohort !== "") {
      constraints.push(where("cohort", "==", filters.cohort));
    }
    if (filters.addedBy && filters.addedBy !== "") {
      constraints.push(where("addedBy", "==", filters.addedBy));
    }

    // ENHANCED SEARCH: Apply server-side case-insensitive search to real-time subscription
    if (filters.search && filters.search.trim() !== "") {
      const searchTerm = filters.search.toLowerCase().trim();

      // Apply same search logic as main query - case-insensitive prefix matching
      constraints.push(where("sellerName_lowercase", ">=", searchTerm));
      constraints.push(
        where("sellerName_lowercase", "<=", searchTerm + "\uf8ff"),
      );
    }

    // ENHANCED SORTING: Apply sorting with tie-breaker for real-time subscription
    if (!filters.search || filters.search.trim() === "") {
      // Primary sort field
      constraints.push(orderBy(sort.field, sort.direction));
      // Tie-breaker: always add createdAt as secondary sort for consistent ordering
      if (sort.field !== "createdAt") {
        constraints.push(orderBy("createdAt", "desc"));
      }
    } else {
      // When searching, order by the search field to support range queries
      constraints.push(orderBy("sellerName_lowercase", "asc"));
      // Add tie-breaker for search results too
      constraints.push(orderBy("createdAt", "desc"));
    }

    // Apply pagination cursor (if provided)
    if (lastDoc) {
      constraints.push(startAfter(lastDoc));
    }

    // Apply limit
    constraints.push(limit(limitCount));

    // Build the paginated query
    const q = query(collection(db, TARGET_LEDGER_COLLECTION), ...constraints);

    return onSnapshot(
      q,
      (querySnapshot) => {
        let data: PpcFarmTargetLedgerData[] = [];
        querySnapshot.forEach((doc) => {
          data.push({
            id: doc.id,
            ...doc.data(),
          } as PpcFarmTargetLedgerData);
        });

        // Server-side search is handled by Firestore queries above
        // No client-side filtering needed for performance

        callback(data);
      },
      (error) => {
        console.error(
          "❌ Error in paginated Target Ledger data subscription:",
          error,
        );
        callback([]);
      },
    );
  },

  /**
   * Get statistics about the Target Ledger data from metadata document
   * PERFORMANCE OPTIMIZED: Uses metadata instead of scanning all 16,000+ documents
   */
  getDataStatistics: async (): Promise<PpcFarmStatistics> => {
    try {
      // Try to get from metadata document first (FAST!)
      const metadataRef = doc(db, "ppcFarm_metadata", "statistics");
      const metadataSnap = await getDoc(metadataRef);

      if (metadataSnap.exists()) {
        const metadata = metadataSnap.data();

        return {
          totalRecords: metadata.totalRecords || 0,
          uniqueCohorts: metadata.uniqueCohorts || 0,
          uniqueSellers: metadata.uniqueSellers || 0,
          lastUpdated: metadata.lastUpdated || null,
        };
      }

      // Fallback: scan documents if metadata doesn't exist (SLOW - only runs once)
      const querySnapshot = await getDocs(
        collection(db, TARGET_LEDGER_COLLECTION),
      );

      const cohorts = new Set<string>();
      const sellers = new Set<string>();
      let lastUpdated: any = null;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.cohort) cohorts.add(data.cohort);
        if (data.sellerName) sellers.add(data.sellerName);
        if (data.dateAdded && (!lastUpdated || data.dateAdded > lastUpdated)) {
          lastUpdated = data.dateAdded;
        }
      });

      const stats: PpcFarmStatistics = {
        totalRecords: querySnapshot.size,
        uniqueCohorts: cohorts.size,
        uniqueSellers: sellers.size,
        lastUpdated,
      };

      // Create metadata document for future use (so this slow scan never happens again)
      const metadataRef2 = doc(db, "ppcFarm_metadata", "statistics");
      await setDoc(metadataRef2, {
        ...stats,
        lastCalculated: new Date(),
      });
      return stats;
    } catch (error) {
      console.error("❌ Error calculating statistics:", error);
      throw error;
    }
  },

  /**
   * Get unique values for filter dropdowns from metadata document
   * This is MUCH faster than scanning all 16,000+ documents
   */
  getFilterOptions: async (): Promise<{
    cohorts: string[];
    addedByUsers: string[];
  }> => {
    try {
      // Try to get from metadata document first (FAST!)
      const metadataRef = doc(db, "ppcFarm_metadata", "filterOptions");
      const metadataSnap = await getDoc(metadataRef);

      if (metadataSnap.exists()) {
        const metadata = metadataSnap.data();

        return {
          cohorts: metadata.cohorts || [],
          addedByUsers: metadata.addedByUsers || [],
        };
      }

      // Fallback: scan documents if metadata doesn't exist (SLOW - only runs once)
      const querySnapshot = await getDocs(
        collection(db, TARGET_LEDGER_COLLECTION),
      );

      const cohorts = new Set<string>();
      const addedByUsers = new Set<string>();

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.cohort) cohorts.add(data.cohort);
        if (data.addedBy) addedByUsers.add(data.addedBy);
      });

      // Sort cohorts numerically (Cohort 1, Cohort 2, etc.)
      const sortedCohorts = Array.from(cohorts).sort((a, b) => {
        // Extract numbers from cohort names (e.g., "Cohort 1" -> 1)
        const aNum = parseInt(a.replace(/\D/g, "")) || 0;
        const bNum = parseInt(b.replace(/\D/g, "")) || 0;
        return aNum - bNum;
      });

      const options = {
        cohorts: sortedCohorts,
        addedByUsers: Array.from(addedByUsers).sort(),
      };

      // Create metadata document for future use (so this slow scan never happens again)
      const metadataRef2 = doc(db, "ppcFarm_metadata", "filterOptions");
      await setDoc(metadataRef2, options);
      return options;
    } catch (error) {
      console.error("❌ Error fetching filter options:", error);
      throw error;
    }
  },

  /**
   * Update filter options metadata (call this when data changes)
   * This should be called by the sync function to keep metadata up-to-date
   */
  updateFilterOptionsMetadata: async (): Promise<void> => {
    try {
      const querySnapshot = await getDocs(
        collection(db, TARGET_LEDGER_COLLECTION),
      );

      const cohorts = new Set<string>();
      const addedByUsers = new Set<string>();

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.cohort) cohorts.add(data.cohort);
        if (data.addedBy) addedByUsers.add(data.addedBy);
      });

      // Sort cohorts numerically
      const sortedCohorts = Array.from(cohorts).sort((a, b) => {
        const aNum = parseInt(a.replace(/\D/g, "")) || 0;
        const bNum = parseInt(b.replace(/\D/g, "")) || 0;
        return aNum - bNum;
      });

      const options = {
        cohorts: sortedCohorts,
        addedByUsers: Array.from(addedByUsers).sort(),
        lastUpdated: new Date(),
      };

      const metadataRef = doc(db, "ppcFarm_metadata", "filterOptions");
      await setDoc(metadataRef, options);
    } catch (error) {
      console.error("❌ Error updating filter options metadata:", error);
      throw error;
    }
  },

  /**
   * Update statistics metadata (call this when data changes)
   * This should be called by the sync function to keep metadata up-to-date
   */
  updateStatisticsMetadata: async (): Promise<void> => {
    try {
      const querySnapshot = await getDocs(
        collection(db, TARGET_LEDGER_COLLECTION),
      );

      const cohorts = new Set<string>();
      const sellers = new Set<string>();
      let lastUpdated: any = null;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.cohort) cohorts.add(data.cohort);
        if (data.sellerName) sellers.add(data.sellerName);
        if (data.dateAdded && (!lastUpdated || data.dateAdded > lastUpdated)) {
          lastUpdated = data.dateAdded;
        }
      });

      const stats = {
        totalRecords: querySnapshot.size,
        uniqueCohorts: cohorts.size,
        uniqueSellers: sellers.size,
        lastUpdated,
        lastCalculated: new Date(),
      };

      const metadataRef = doc(db, "ppcFarm_metadata", "statistics");
      await setDoc(metadataRef, stats);
    } catch (error) {
      console.error("❌ Error updating statistics metadata:", error);
      throw error;
    }
  },

  /**
   * Update all metadata documents (call this when data changes)
   * This is a convenience method to update both filter options and statistics
   */
  updateAllMetadata: async (): Promise<void> => {
    try {
      // Run both metadata updates in parallel for efficiency
      await Promise.all([
        ppcFarmService.updateFilterOptionsMetadata(),
        ppcFarmService.updateStatisticsMetadata(),
      ]);
    } catch (error) {
      console.error("❌ Error updating metadata documents:", error);
      throw error;
    }
  },

  /**
   * Fetch Target Ledger metrics for the app hub card
   * Updated to use the correct appMetadata document with current data
   */
  getTargetLedgerMetrics: async (): Promise<TargetLedgerMetrics> => {
    try {
      // Fetch from the correct appMetadata document that has up-to-date information
      const metadataRef = doc(db, "ppcFarm_metadata", "appMetadata");
      const metadataSnap = await getDoc(metadataRef);

      if (metadataSnap.exists()) {
        const metadata = metadataSnap.data();
        const targetLedgerStats = metadata.ledgerStats?.targetLedger;
        const globalFilters = metadata.globalFilterOptions;

        return {
          totalSellers: targetLedgerStats?.uniqueSellers || 0,
          totalCohorts: globalFilters?.cohorts?.length || 0,
          lastSync: metadata.lastSyncTime || null,
        };
      }

      // Fallback to old metadata documents if appMetadata doesn't exist
      const [filterOptionsDoc, statisticsDoc] = await Promise.all([
        getDoc(doc(db, "ppcFarm_metadata", "filterOptions")),
        getDoc(doc(db, "ppcFarm_metadata", "statistics")),
      ]);

      let totalCohorts = 0;
      let lastSync: string | null = null;
      let uniqueSellers = 0;

      // Get cohorts and last sync from filterOptions
      if (filterOptionsDoc.exists()) {
        const data = filterOptionsDoc.data();
        totalCohorts = data.cohorts?.length || 0;
        lastSync = data.lastUpdated || null;
      }

      // Get unique sellers from statistics
      if (statisticsDoc.exists()) {
        const data = statisticsDoc.data();
        uniqueSellers = data.uniqueSellers || 0;
      }

      return {
        totalSellers: uniqueSellers,
        totalCohorts,
        lastSync,
      };
    } catch (error) {
      console.error("Error fetching Target Ledger metrics:", error);
      return {
        totalSellers: 0,
        totalCohorts: 0,
        lastSync: null,
      };
    }
  },

  /**
   * Generic method to get ledger data for any ledger type
   * This enables code reuse across all three ledgers
   */
  getLedgerData: async <T extends PpcFarmLedgerData>(
    ledgerType: LedgerType,
    options?: {
      filters?: Record<string, any>;
      sort?: { field: string; direction: "asc" | "desc" };
      pagination?: PpcFarmPaginationOptions;
      lastDoc?: QueryDocumentSnapshot<DocumentData>;
    },
  ): Promise<{
    data: T[];
    lastDoc?: QueryDocumentSnapshot<DocumentData>;
    hasMore: boolean;
    totalCount: number;
  }> => {
    const collectionName = getCollectionName(ledgerType);
    const {
      filters = {},
      sort = getDefaultSort(ledgerType),
      pagination = { page: 1, limit: DEFAULT_PAGE_SIZE, total: 0 },
      lastDoc,
    } = options || {};

    try {
      const pageSize = Math.min(pagination.limit, MAX_PAGE_SIZE);

      // SMART QUERY STRATEGY: Always use Netlify function for Target and Mailing Ledgers
      // The function now handles all scenarios efficiently with a single Firestore query
      if (
        ledgerType === LedgerType.TARGET ||
        ledgerType === LedgerType.MAILING
      ) {
        return await executeNetlifySmartQuery<T>(
          ledgerType,
          filters,
          sort,
          pagination,
          pageSize,
          lastDoc,
        );
      }

      // COHORT LEDGER: Continue using direct Firestore queries (no search functionality needed)
      const constraints: QueryConstraint[] = [];

      // Apply filters based on ledger type (excluding search)
      applyFiltersToQuery(constraints, filters, ledgerType);

      // ENHANCED SORTING: Apply sorting (only for Cohort Ledger now, since Target/Mailing use Netlify)
      if (sort.field) {
        // Handle cohort sorting using cohortNumberNumeric field for proper numerical sorting
        if (sort.field === "cohortNumber") {
          // For cohort numbers, use the new cohortNumberNumeric field for proper numerical sorting
          constraints.push(orderBy("cohortNumberNumeric", sort.direction));
          // Add tie-breaker
          constraints.push(orderBy("createdAt", "desc"));
        } else {
          // Primary sort field
          constraints.push(orderBy(sort.field, sort.direction));
          // Tie-breaker: always add createdAt as secondary sort for consistent ordering
          if (sort.field !== "createdAt") {
            constraints.push(orderBy("createdAt", "desc"));
          }
        }
      }

      // Apply pagination cursor
      if (lastDoc) {
        constraints.push(startAfter(lastDoc));
      }

      // Apply limit (get one extra to check if there are more)
      constraints.push(limit(pageSize + 1));

      // Build and execute query
      const dataQuery = query(collection(db, collectionName), ...constraints);
      const [querySnapshot, totalCount] = await Promise.all([
        getDocs(dataQuery),
        ppcFarmService.getLedgerTotalCount(ledgerType, filters),
      ]);

      const docs = querySnapshot.docs;
      const hasMore = docs.length > pageSize;

      // Remove the extra document if it exists
      if (hasMore) {
        docs.pop();
      }

      let data: T[] = docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as T,
      );

      const newLastDoc = docs.length > 0 ? docs[docs.length - 1] : undefined;

      return {
        data,
        lastDoc: newLastDoc,
        hasMore,
        totalCount,
      };
    } catch (error) {
      console.error(`❌ Error fetching ${ledgerType} ledger data:`, error);
      throw new Error(
        `Failed to fetch ${ledgerType} ledger data: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  },

  /**
   * Get total count for any ledger type
   */
  getLedgerTotalCount: async (
    ledgerType: LedgerType,
    filters: Record<string, any> = {},
  ): Promise<number> => {
    try {
      // SMART QUERY STRATEGY: Use Netlify function for Target and Mailing Ledgers
      if (
        ledgerType === LedgerType.TARGET ||
        ledgerType === LedgerType.MAILING
      ) {
        // For Target/Mailing ledgers, the count is returned by the Netlify function
        // This is handled in the main query, so we return 0 here as a placeholder
        // The actual count comes from the executeNetlifySmartQuery result
        return 0;
      }

      // COHORT LEDGER: Continue using direct Firestore count queries
      const collectionName = getCollectionName(ledgerType);
      const constraints: QueryConstraint[] = [];

      // Apply filters based on ledger type (excluding search)
      applyFiltersToQuery(constraints, filters, ledgerType);

      let totalCount = 0;

      if (constraints.length > 0) {
        const q = query(collection(db, collectionName), ...constraints);
        const snapshot = await getCountFromServer(q);
        totalCount = snapshot.data().count;
      } else {
        // No constraints, get total count
        const q = query(collection(db, collectionName));
        const snapshot = await getCountFromServer(q);
        totalCount = snapshot.data().count;
      }

      return totalCount;
    } catch (error) {
      console.error(
        `❌ Error getting ${ledgerType} ledger total count:`,
        error,
      );
      return 0;
    }
  },

  /**
   * Get statistics for any ledger type
   */
  getLedgerStatistics: async (ledgerType: LedgerType): Promise<any> => {
    try {
      const metadataRef = doc(db, "ppcFarm_metadata", "appMetadata");
      const metadataSnap = await getDoc(metadataRef);

      if (metadataSnap.exists()) {
        const metadata = metadataSnap.data();
        const ledgerStats = metadata.ledgerStats?.[`${ledgerType}Ledger`];

        if (ledgerStats) {
          // For mailing ledger, return the pre-calculated stats
          if (ledgerType === LedgerType.MAILING) {
            return {
              totalRecords: ledgerStats.totalRecords || 0,
              mailersSent: ledgerStats.mailersSent || 0,
              mailersPending: ledgerStats.mailersPending || 0,
              lastUpdated: metadata.lastSyncTime || null,
            };
          }

          // For other ledgers, return standard format
          return {
            totalRecords: ledgerStats.totalRecords || 0,
            uniqueCohorts: ledgerStats.uniqueCohorts || 0,
            uniqueSellers: ledgerStats.uniqueSellers || 0,
            lastUpdated: metadata.lastSyncTime || null,
          };
        }
      }

      // Fallback: return empty statistics
      return {
        totalRecords: 0,
        uniqueCohorts: 0,
        uniqueSellers: 0,
        lastUpdated: undefined,
      };
    } catch (error) {
      console.error(`❌ Error getting ${ledgerType} ledger statistics:`, error);
      return {
        totalRecords: 0,
        uniqueCohorts: 0,
        uniqueSellers: 0,
        lastUpdated: undefined,
      };
    }
  },

  /**
   * Get filter options for any ledger type
   */
  getLedgerFilterOptions: async (
    ledgerType: LedgerType,
  ): Promise<Record<string, string[]>> => {
    try {
      const metadataRef = doc(db, "ppcFarm_metadata", "appMetadata");
      const metadataSnap = await getDoc(metadataRef);

      if (metadataSnap.exists()) {
        const metadata = metadataSnap.data();
        const globalFilters = metadata.globalFilterOptions || {};
        const cohortLedgerFilters = metadata.cohortLedgerFilterOptions || {};

        // Return relevant filter options based on ledger type
        switch (ledgerType) {
          case LedgerType.TARGET:
            return {
              cohort: globalFilters.cohorts || [],
              addedBy: globalFilters.addedByUsers || [],
            };
          case LedgerType.COHORT:
            return {
              cohort: globalFilters.cohorts || [],
              yourName:
                cohortLedgerFilters.yourNames ||
                cohortLedgerFilters.creators ||
                [],
              yourNames:
                cohortLedgerFilters.yourNames ||
                cohortLedgerFilters.creators ||
                [], // Provide both field names for compatibility
              creators:
                cohortLedgerFilters.creators ||
                cohortLedgerFilters.yourNames ||
                [], // Additional alias for compatibility
            };
          case LedgerType.MAILING:
            return {
              cohort: globalFilters.cohorts || [],
              addressingType:
                metadata.mailingLedgerFilterOptions?.addressingTypes || [],
              addedBy: globalFilters.addedByUsers || [],
              includeInMailingCostDashboard: ["Yes", "No"], // Hardcoded options for Include in Dashboard filter
            };
          default:
            return {};
        }
      }

      return {};
    } catch (error) {
      console.error(
        `❌ Error getting ${ledgerType} ledger filter options:`,
        error,
      );
      return {};
    }
  },

  /**
   * Get comprehensive dashboard metrics for the main dashboard page
   * Fetches all key metrics from the appMetadata document and calculates conversion rate from actual cohort data
   */
  getDashboardMetrics: async (): Promise<{
    totalSellers: number;
    totalCohorts: number;
    mailersSent: number;
    pendingShipments: number;
    overallConversionRate: number;
    lastSync: string | null;
  }> => {
    try {
      // Fetch metadata for most metrics
      const metadataRef = doc(db, "ppcFarm_metadata", "appMetadata");
      const metadataSnap = await getDoc(metadataRef);

      // PERFORMANCE OPTIMIZED: Get conversion rate from metadata instead of scanning all cohort documents
      let overallConversionRate = 0;
      
      // Try to get pre-calculated conversion rate from metadata first
      if (metadataSnap.exists()) {
        const metadata = metadataSnap.data();
        const cohortLedgerStats = metadata.ledgerStats?.cohortLedger;
        
        if (cohortLedgerStats?.totalCount && cohortLedgerStats?.totalSignedClients) {
          overallConversionRate = cohortLedgerStats.totalCount > 0
            ? (cohortLedgerStats.totalSignedClients / cohortLedgerStats.totalCount) * 100
            : 0;
        }
      }
      
      // Fallback: If metadata doesn't have conversion rate data, calculate it (but this should be rare)
      if (overallConversionRate === 0) {
        try {
          const cohortQuery = query(collection(db, COHORT_LEDGER_COLLECTION));
          const cohortSnapshot = await getDocs(cohortQuery);

          if (!cohortSnapshot.empty) {
            let totalCount = 0;
            let totalSignedClients = 0;

            cohortSnapshot.forEach((doc) => {
              const cohort = doc.data();
              totalCount += cohort.count || 0;
              totalSignedClients += cohort.signedClients || 0;
            });

            overallConversionRate =
              totalCount > 0 ? (totalSignedClients / totalCount) * 100 : 0;
          }
        } catch (error) {
          console.warn('Failed to calculate conversion rate from cohort data:', error);
          overallConversionRate = 0;
        }
      }

      if (metadataSnap.exists()) {
        const metadata = metadataSnap.data();
        const targetLedgerStats = metadata.ledgerStats?.targetLedger;
        const mailingLedgerStats = metadata.ledgerStats?.mailingLedger;
        const globalFilters = metadata.globalFilterOptions;

        return {
          totalSellers: targetLedgerStats?.uniqueSellers || 0,
          totalCohorts: globalFilters?.cohorts?.length || 0,
          mailersSent: mailingLedgerStats?.mailersSent || 0,
          pendingShipments: mailingLedgerStats?.mailersPending || 0,
          overallConversionRate: overallConversionRate, // Use calculated value from actual data
          lastSync: metadata.lastSyncTime || null,
        };
      }

      // Fallback: still return calculated conversion rate even if metadata doesn't exist
      return {
        totalSellers: 0,
        totalCohorts: 0,
        mailersSent: 0,
        pendingShipments: 0,
        overallConversionRate: overallConversionRate, // Use calculated value from actual data
        lastSync: null,
      };
    } catch (error) {
      console.error("Error fetching dashboard metrics:", error);
      return {
        totalSellers: 0,
        totalCohorts: 0,
        mailersSent: 0,
        pendingShipments: 0,
        overallConversionRate: 0,
        lastSync: null,
      };
    }
  },

  /**
   * Trigger bulk import for all PPC Farm ledgers
   * This calls the Firebase Function to import all data from Google Sheets
   */
  bulkImportAllPpcFarmLedgers: async (): Promise<{
    success: boolean;
    targetLedger?: {
      totalRows: number;
      validRows: number;
      processedRows: number;
      skippedRows: number;
      dataQualityIssues: number;
      filteredOutRows: number;
      batches: number;
    };
    cohortLedger?: {
      totalRows: number;
      validRows: number;
      processedRows: number;
      batches: number;
    };
    mailingLedger?: {
      totalRows: number;
      validRows: number;
      processedRows: number;
      batches: number;
    };
    ledgerMetrics?: any[];
  }> => {
    try {
      const functions = getFunctions();
      const bulkImportFunction = httpsCallable(functions, 'bulkImportAllPpcFarmLedgers');
      
      console.log("🚀 Calling Firebase Function: bulkImportAllPpcFarmLedgers");
      const result = await bulkImportFunction();
      
      console.log("✅ Bulk import completed:", result.data);
      return result.data as any;
    } catch (error) {
      console.error("❌ Error calling bulk import function:", error);
      throw new Error(
        `Failed to trigger bulk import: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  },

  /**
   * Get enhanced dashboard metrics combining existing metrics with new analytics
   * This provides comprehensive dashboard data including historical trends and source breakdowns
   */
  getDashboardMetricsEnhanced: async (): Promise<DashboardMetricsEnhanced> => {
    try {
      console.log('📊 Fetching enhanced dashboard metrics...');
      
      // Fetch analytics and pipeline status in parallel for performance
      const [analytics, pipelineStatus] = await Promise.all([
        PpcFarmAnalyticsService.getAnalytics(),
        ppcFarmService.getKeepaPipelineStatus()
      ]);

      // Get current counts from collections
      const [sellerIdsSnapshot, sellerDetailsSnapshot] = await Promise.all([
        getCountFromServer(query(collection(db, PPC_FARM_COLLECTIONS.SELLER_IDS))),
        getCountFromServer(query(collection(db, PPC_FARM_COLLECTIONS.SELLER_DETAILS)))
      ]);

      const [unprocessedSnapshot, processedSnapshot] = await Promise.all([
        getCountFromServer(query(collection(db, PPC_FARM_COLLECTIONS.SELLER_IDS), where('status', '==', 'unprocessed'))),
        getCountFromServer(query(collection(db, PPC_FARM_COLLECTIONS.SELLER_IDS), where('status', '==', 'processed')))
      ]);

      const totalSellerIds = sellerIdsSnapshot.data().count;
      const totalSellerDetails = sellerDetailsSnapshot.data().count;
      const unprocessedSellerIds = unprocessedSnapshot.data().count;
      const processedSellerIds = processedSnapshot.data().count;

      // Default analytics if not available
      const defaultAnalytics = {
        totals: {
          newSellersFound: 0,
          sellersProcessed: 0,
          competitorsFound: 0,
          totalSellersFromKeepa: 0,
          rawScrapeTotalSellers: 0,
          competitorsTotalSellers: 0
        },
        sourceBreakdown: {
          newSellersFound: { keepa_topseller_daily: 0, raw_scrape_g_sheet: 0, competitor_extraction: 0 },
          sellersProcessed: { keepa_topseller_daily: 0, raw_scrape_g_sheet: 0, competitor_extraction: 0 },
          competitorsFound: { keepa_topseller_daily: 0, raw_scrape_g_sheet: 0, competitor_extraction: 0 }
        },
        dailyStats: []
      };

      const analyticsData = analytics || defaultAnalytics;

      // Calculate trends from daily statistics
      const newSellersFoundTrends = PpcFarmAnalyticsService.calculateTrends(analyticsData.dailyStats, 'newSellersFound');
      const sellersProcessedTrends = PpcFarmAnalyticsService.calculateTrends(analyticsData.dailyStats, 'sellersProcessed');
      const competitorsFoundTrends = PpcFarmAnalyticsService.calculateTrends(analyticsData.dailyStats, 'competitorsFound');

      // Calculate processing velocity
      const velocity = PpcFarmAnalyticsService.calculateVelocity(analyticsData.dailyStats);

      const enhancedMetrics: DashboardMetricsEnhanced = {
        // Existing metrics
        totalSellerIds,
        unprocessedSellerIds,
        processedSellerIds,
        totalSellerDetails,

        // New analytics metrics
        analytics: {
          trends: {
            newSellersFound: newSellersFoundTrends,
            sellersProcessed: sellersProcessedTrends,
            competitorsFound: competitorsFoundTrends
          },
          sourceTotals: {
            totalSellersFromKeepa: (analyticsData.totals as any).sourceCounts?.keepa_topseller_daily || 0,
            rawScrapeTotalSellers: (analyticsData.totals as any).sourceCounts?.raw_scrape_g_sheet || 0,
            competitorsTotalSellers: (analyticsData.totals as any).sourceCounts?.competitor_extraction || 0
          },
          velocity
        },

        // Pipeline status
        pipelineStatus,
        lastUpdated: analytics?.lastUpdated || new Date() as any
      };

      console.log('✅ Enhanced dashboard metrics fetched successfully');
      return enhancedMetrics;

    } catch (error) {
      console.error('❌ Error fetching enhanced dashboard metrics:', error);
      
      // Return fallback metrics to prevent dashboard from breaking
      return {
        totalSellerIds: 0,
        unprocessedSellerIds: 0,
        processedSellerIds: 0,
        totalSellerDetails: 0,
        analytics: {
          trends: {
            newSellersFound: { last24h: 0, last7d: 0, last30d: 0 },
            sellersProcessed: { last24h: 0, last7d: 0, last30d: 0 },
            competitorsFound: { last24h: 0, last7d: 0, last30d: 0 }
          },
          sourceTotals: {
            totalSellersFromKeepa: 0,
            rawScrapeTotalSellers: 0,
            competitorsTotalSellers: 0
          },
          velocity: {
            avgSellersProcessedPerDay: 0,
            avgCompetitorsFoundPerDay: 0
          }
        },
        lastUpdated: new Date() as any
      };
    }
  },

  /**
   * Get Keepa pipeline status for dashboard display
   * Now reads from the consolidated pipelineAnalytics document
   */
  getKeepaPipelineStatus: async (): Promise<KeepaPipelineStatus | undefined> => {
    try {
      const analyticsRef = doc(db, PPC_FARM_COLLECTIONS.METADATA, PPC_FARM_DOCUMENTS.PIPELINE_ANALYTICS);
      const analyticsSnap = await getDoc(analyticsRef);
      
      if (analyticsSnap.exists()) {
        const analyticsData = analyticsSnap.data() as any;
        return analyticsData.pipelineStatus as KeepaPipelineStatus;
      }
      
      return undefined;
    } catch (error) {
      console.error('❌ Error fetching pipeline status from analytics:', error);
      return undefined;
    }
  },

  /**
   * Get enhanced dashboard metrics that combines pipeline status and analytics data
   * This method provides all data needed for the new analytics dashboard UI
   */
  getEnhancedDashboardMetrics: async (): Promise<EnhancedDashboardMetrics> => {
    try {
      console.log('📊 Fetching enhanced dashboard metrics for new UI...');
      
      // Fetch analytics and pipeline status in parallel
      const [analytics, pipelineStatus] = await Promise.all([
        PpcFarmAnalyticsService.getAnalytics(),
        ppcFarmService.getKeepaPipelineStatus()
      ]);

      // Get current queue count
      const unprocessedSnapshot = await getCountFromServer(
        query(collection(db, PPC_FARM_COLLECTIONS.SELLER_IDS), where('status', '==', 'unprocessed'))
      );
      const sellersInQueue = unprocessedSnapshot.data().count;

      // Calculate estimated time to clear (assuming 15 sellers/min processing rate)
      const processingRate = 15; // sellers per minute
      const hoursToProcess = Math.ceil(sellersInQueue / (processingRate * 60));
      const daysToProcess = Math.ceil(hoursToProcess / 24);
      const estimatedTimeToClear = daysToProcess > 1
        ? `~${daysToProcess} days to clear`
        : hoursToProcess > 1
        ? `~${hoursToProcess} hours to clear`
        : `~${Math.ceil(sellersInQueue / processingRate)} min to clear`;

      // Determine overall status
      let overallStatus: 'healthy' | 'warning' | 'error' = 'healthy';
      if (pipelineStatus) {
        const lastRunHours = (Date.now() - pipelineStatus.ingestion.lastRunTimestamp.toMillis()) / (1000 * 60 * 60);
        const isIngestionStale = lastRunHours > 26;
        const isIngestionFailed = pipelineStatus.ingestion.lastRunOutcome === 'failure';
        const isProcessingStalled = pipelineStatus.enrichment.processedLastRun === 0;
        
        if (isIngestionFailed || isProcessingStalled) {
          overallStatus = 'error';
        } else if (isIngestionStale) {
          overallStatus = 'warning';
        }
      }

      // Default analytics if not available
      const defaultAnalytics = {
        totals: {
          sellersProcessed: 0,
          totalSellersFromKeepa: 0,
          rawScrapeTotalSellers: 0,
          competitorsTotalSellers: 0
        },
        dailyStats: []
      };

      const analyticsData = analytics || defaultAnalytics;

      // Calculate source totals and percentages
      // Access nested under totals.sourceCounts with proper type casting
      const totals = (analyticsData as any).totals || {};
      const sourceCounts = totals.sourceCounts || {};
      
      console.log('Debug source counts access:', {
        totals,
        sourceCounts,
        analyticsDataStructure: Object.keys(analyticsData)
      });
      
      const totalEnrichedSellers = totals.totalSellersInMaster || 0;
      const keepaCount = sourceCounts.keepa_topseller_daily || 0;
      const rawScrapeCount = sourceCounts.raw_scrape_g_sheet || 0;
      const competitorCount = sourceCounts.competitor_extraction || 0;
      const targetLedgerCount = sourceCounts.target_ledger_migration || 0;
      
      console.log('Parsed source counts for dashboard:', {
        totalEnrichedSellers,
        keepaCount,
        rawScrapeCount,
        competitorCount,
        targetLedgerCount
      });

      const fromKeepaTopSellers = {
        count: keepaCount,
        percentage: totalEnrichedSellers > 0 ? (keepaCount / totalEnrichedSellers) * 100 : 0
      };

      const fromCompetitors = {
        count: competitorCount,
        percentage: totalEnrichedSellers > 0 ? (competitorCount / totalEnrichedSellers) * 100 : 0
      };

      const fromRawScrape = {
        count: rawScrapeCount,
        percentage: totalEnrichedSellers > 0 ? (rawScrapeCount / totalEnrichedSellers) * 100 : 0
      };

      const fromTargetLedger = {
        count: targetLedgerCount,
        percentage: totalEnrichedSellers > 0 ? (targetLedgerCount / totalEnrichedSellers) * 100 : 0
      };

      // Calculate rolling trends from daily statistics
      const newSellersFoundTrends = PpcFarmAnalyticsService.calculateTrends(analyticsData.dailyStats, 'newSellersFound');
      const sellersProcessedTrends = PpcFarmAnalyticsService.calculateTrends(analyticsData.dailyStats, 'sellersProcessed');

      // Get 24h discovery metrics using the correct data sources
      let keepaDiscovery24h = 0;
      let competitorDiscovery24h = 0;
      
      // For Keepa Discovery 24h: Use pipeline status (shows daily ingestion run results)
      if (pipelineStatus) {
        keepaDiscovery24h = pipelineStatus.ingestion?.newSellersAdded || 0;
      }
      
      // For Competitor Discovery 24h: Use analytics daily stats (shows actual 24h totals)
      // This is more accurate than pipeline status which only shows last minute run
      const competitorTrends = PpcFarmAnalyticsService.calculateTrends(analyticsData.dailyStats, 'competitorsFound');
      competitorDiscovery24h = competitorTrends.last24h;
      
      console.log('📊 24h discovery metrics (using competitorsFound field):', {
        keepaDiscovery24h: `${keepaDiscovery24h} (from pipeline status - daily ingestion)`,
        competitorDiscovery24h: `${competitorDiscovery24h} (from analytics daily stats - competitorsFound field)`,
        pipelineStatusAvailable: !!pipelineStatus,
        analyticsDataAvailable: !!analyticsData,
        dailyStatsLength: analyticsData.dailyStats?.length || 0
      });

      const enhancedMetrics = {
        // Live Pipeline Status
        overallStatus,
        sellersInQueue,
        keepaDiscovery24h,
        competitorDiscovery24h,
        
        // Seller Database Overview
        totalEnrichedSellers,
        fromKeepaTopSellers,
        fromCompetitors,
        fromRawScrape,
        fromTargetLedger,
        
        // Performance Trends (Rolling Averages)
        newSellersFound: newSellersFoundTrends,
        sellersProcessed: sellersProcessedTrends,
        
        // Additional metadata
        lastUpdated: analytics?.lastUpdated || Timestamp.now(),
        estimatedTimeToClear
      };

      console.log('✅ Enhanced dashboard metrics fetched successfully');
      return enhancedMetrics;

    } catch (error) {
      console.error('❌ Error fetching enhanced dashboard metrics:', error);
      
      // Return fallback metrics to prevent dashboard from breaking
      return {
        overallStatus: 'error' as const,
        sellersInQueue: 0,
        keepaDiscovery24h: 0,
        competitorDiscovery24h: 0,
        totalEnrichedSellers: 0,
        fromKeepaTopSellers: { count: 0, percentage: 0 },
        fromCompetitors: { count: 0, percentage: 0 },
        fromRawScrape: { count: 0, percentage: 0 },
        fromTargetLedger: { count: 0, percentage: 0 },
        newSellersFound: { last24h: 0, last7d: 0, last30d: 0 },
        sellersProcessed: { last24h: 0, last7d: 0, last30d: 0 },
        lastUpdated: Timestamp.now(),
        estimatedTimeToClear: 'Unknown'
      };
    }
  },
};

// Helper functions for generic operations

/**
 * Get collection name for ledger type
 */
function getCollectionName(ledgerType: LedgerType): string {
  switch (ledgerType) {
    case LedgerType.TARGET:
      return TARGET_LEDGER_COLLECTION;
    case LedgerType.COHORT:
      return COHORT_LEDGER_COLLECTION;
    case LedgerType.MAILING:
      return MAILING_LEDGER_COLLECTION;
    default:
      throw new Error(`Unknown ledger type: ${ledgerType}`);
  }
}

/**
 * Get default sort options for ledger type
 */
function getDefaultSort(ledgerType: LedgerType): {
  field: string;
  direction: "asc" | "desc";
} {
  switch (ledgerType) {
    case LedgerType.TARGET:
      return { field: "dateAdded", direction: "desc" };
    case LedgerType.COHORT:
      return { field: "dateCreated", direction: "desc" };
    case LedgerType.MAILING:
      return { field: "dateEntered", direction: "desc" };
    default:
      return { field: "dateAdded", direction: "desc" };
  }
}

/**
 * Custom sorting function for cohort numbers to handle numerical sorting
 */
function sortCohortNumbers(a: any, b: any, direction: "asc" | "desc"): number {
  // Extract numbers from cohort strings (e.g., "Cohort 1" -> 1, "Cohort 10" -> 10)
  const extractNumber = (cohortStr: string): number => {
    const match = cohortStr.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };

  const numA = extractNumber(a.cohortNumber || a.cohort || "");
  const numB = extractNumber(b.cohortNumber || b.cohort || "");

  if (direction === "asc") {
    return numA - numB;
  } else {
    return numB - numA;
  }
}

/**
 * Apply filters to query based on ledger type
 */
function applyFiltersToQuery(
  constraints: QueryConstraint[],
  filters: Record<string, any>,
  ledgerType: LedgerType,
): void {
  switch (ledgerType) {
    case LedgerType.TARGET:
      if (filters.cohort && filters.cohort !== "") {
        constraints.push(where("cohort", "==", filters.cohort));
      }
      if (filters.addedBy && filters.addedBy !== "") {
        constraints.push(where("addedBy", "==", filters.addedBy));
      }
      // NOTE: Search is handled separately to avoid Firestore range query limitations
      // When search is active, we use a different query strategy
      break;

    case LedgerType.COHORT:
      if (filters.cohort && filters.cohort !== "") {
        constraints.push(where("cohortNumber", "==", filters.cohort));
      }
      if (filters.yourName && filters.yourName !== "") {
        constraints.push(where("yourName", "==", filters.yourName));
      }
      if (filters.isTriedAndTrue && filters.isTriedAndTrue !== "") {
        const isTriedAndTrue = filters.isTriedAndTrue === "true";
        constraints.push(where("isTriedAndTrue", "==", isTriedAndTrue));
      }
      // NOTE: Cohort ledger doesn't have search functionality per requirements
      break;

    case LedgerType.MAILING:
      if (filters.cohort && filters.cohort !== "") {
        constraints.push(where("cohort", "==", filters.cohort));
      }
      if (filters.addressingType && filters.addressingType !== "") {
        constraints.push(where("addressingType", "==", filters.addressingType));
      }
      if (filters.addedBy && filters.addedBy !== "") {
        constraints.push(where("addedBy", "==", filters.addedBy));
      }
      if (
        filters.includeInMailingCostDashboard &&
        filters.includeInMailingCostDashboard !== ""
      ) {
        const includeValue = filters.includeInMailingCostDashboard === "Yes";
        constraints.push(
          where("includeInMailingCostDashboard", "==", includeValue),
        );
      }
      // NOTE: Search is handled separately to avoid Firestore range query limitations
      // When search is active, we use a different query strategy
      break;
  }
}

/**
 * Execute Netlify smart query with cursor-based pagination for all scenarios
 * Uses the smart query builder that relies entirely on Firestore's native querying capabilities
 */
async function executeNetlifySmartQuery<T extends PpcFarmLedgerData>(
  ledgerType: LedgerType,
  filters: Record<string, any>,
  sort: { field: string; direction: "asc" | "desc" },
  pagination: PpcFarmPaginationOptions,
  pageSize: number,
  lastDoc?: QueryDocumentSnapshot<DocumentData>,
): Promise<{
  data: T[];
  lastDoc?: QueryDocumentSnapshot<DocumentData>;
  hasMore: boolean;
  totalCount: number;
}> {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Get Firebase Auth token
    const token = await user.getIdToken();

    // Convert lastDoc to cursor string if available
    let cursor: string | undefined;
    if (lastDoc) {
      cursor = lastDoc.id; // Use document ID as cursor
    }

    // Prepare request payload for smart query builder
    const requestBody = {
      ledgerType: ledgerType === LedgerType.TARGET ? "target" : "mailing",
      filters,
      sort,
      pagination: {
        page: pagination.page,
        limit: pageSize,
      },
      cursor, // Add cursor for pagination
    };

    console.log(
      `🔧 Executing smart query via Netlify function for ${ledgerType} ledger with cursor: ${cursor || "none"}`,
    );

    // Call Netlify function
    const response = await fetch("/.netlify/functions/ppcFarmSearch", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Smart query request failed: ${response.status} ${errorText}`,
      );
    }

    const result = await response.json();

    // Handle error responses from the function
    if (result.error) {
      console.warn(`⚠️ Netlify function returned error: ${result.error}`);
      // Return empty result with error message
      return {
        data: [],
        lastDoc: undefined,
        hasMore: false,
        totalCount: 0,
      };
    }

    // The smart query builder handles all scenarios with cursor-based pagination
    // For cursor-based pagination, we store the nextCursor from the Netlify function
    // and convert it back to a mock lastDoc for the hook to use
    let mockLastDoc: QueryDocumentSnapshot<DocumentData> | undefined;
    if (result.hasMore && result.nextCursor) {
      // Create a minimal mock document that only has the ID property
      // This is sufficient for the cursor-based pagination to work
      mockLastDoc = {
        id: result.nextCursor,
      } as QueryDocumentSnapshot<DocumentData>;
      console.log(`🔄 Created mock lastDoc with cursor: ${result.nextCursor}`);
    } else {
      console.log(
        `🔄 No nextCursor provided, hasMore: ${result.hasMore}, nextCursor: ${result.nextCursor}`,
      );
    }

    console.log(`📊 Netlify function response:`, {
      dataLength: result.data?.length || 0,
      totalCount: result.totalCount,
      hasMore: result.hasMore,
      nextCursor: result.nextCursor,
    });

    return {
      data: result.data || [],
      lastDoc: mockLastDoc, // Preserve cursor for next request
      hasMore: result.hasMore || false,
      totalCount: result.totalCount || 0,
    };
  } catch (error) {
    console.error(
      `❌ Error executing Netlify smart query for ${ledgerType}:`,
      error,
    );
    throw new Error(
      `Failed to execute smart query: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
}

/**
 * Execute search-optimized query that handles Firestore's range query limitations
 * This function uses a search-first approach to avoid multiple range queries
 */
async function executeSearchQuery<T extends PpcFarmLedgerData>(
  ledgerType: LedgerType,
  filters: Record<string, any>,
  sort: { field: string; direction: "asc" | "desc" },
  pagination: PpcFarmPaginationOptions,
  lastDoc?: QueryDocumentSnapshot<DocumentData>,
  pageSize: number = DEFAULT_PAGE_SIZE,
): Promise<{
  data: T[];
  lastDoc?: QueryDocumentSnapshot<DocumentData>;
  hasMore: boolean;
  totalCount: number;
}> {
  const collectionName = getCollectionName(ledgerType);
  const searchTerm = filters.search.toLowerCase().trim();

  try {
    // Build search-optimized query constraints
    const constraints: QueryConstraint[] = [];

    // Apply search as the primary range query
    switch (ledgerType) {
      case LedgerType.TARGET:
        // Search by seller name (primary search field)
        constraints.push(where("sellerName_lowercase", ">=", searchTerm));
        constraints.push(
          where("sellerName_lowercase", "<=", searchTerm + "\uf8ff"),
        );
        // Order by search field first (required for range queries)
        constraints.push(orderBy("sellerName_lowercase", "asc"));
        // Add tie-breaker
        constraints.push(orderBy("createdAt", "desc"));
        break;

      case LedgerType.MAILING:
        // Search by mailer ID (primary search field)
        constraints.push(where("mailerId", ">=", searchTerm));
        constraints.push(where("mailerId", "<=", searchTerm + "\uf8ff"));
        // Order by search field first (required for range queries)
        constraints.push(orderBy("mailerId", "asc"));
        // Add tie-breaker
        constraints.push(orderBy("createdAt", "desc"));
        break;

      default:
        throw new Error(`Search not supported for ${ledgerType} ledger`);
    }

    // Apply equality filters only (no additional range queries allowed)
    switch (ledgerType) {
      case LedgerType.TARGET:
        if (filters.cohort && filters.cohort !== "") {
          constraints.push(where("cohort", "==", filters.cohort));
        }
        if (filters.addedBy && filters.addedBy !== "") {
          constraints.push(where("addedBy", "==", filters.addedBy));
        }
        break;

      case LedgerType.MAILING:
        if (filters.cohort && filters.cohort !== "") {
          constraints.push(where("cohort", "==", filters.cohort));
        }
        if (filters.addressingType && filters.addressingType !== "") {
          constraints.push(
            where("addressingType", "==", filters.addressingType),
          );
        }
        if (filters.addedBy && filters.addedBy !== "") {
          constraints.push(where("addedBy", "==", filters.addedBy));
        }
        if (
          filters.includeInMailingCostDashboard &&
          filters.includeInMailingCostDashboard !== ""
        ) {
          const includeValue = filters.includeInMailingCostDashboard === "Yes";
          constraints.push(
            where("includeInMailingCostDashboard", "==", includeValue),
          );
        }
        break;
    }

    // Apply pagination cursor
    if (lastDoc) {
      constraints.push(startAfter(lastDoc));
    }

    // Apply limit (get one extra to check if there are more)
    constraints.push(limit(pageSize + 1));

    // Execute search query
    const searchQuery = query(collection(db, collectionName), ...constraints);
    const [querySnapshot, totalCount] = await Promise.all([
      getDocs(searchQuery),
      getSearchTotalCount(ledgerType, filters),
    ]);

    const docs = querySnapshot.docs;
    const hasMore = docs.length > pageSize;

    // Remove the extra document if it exists
    if (hasMore) {
      docs.pop();
    }

    let data: T[] = docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as T,
    );

    // NOTE: For search queries, we maintain search relevance order
    // The original sort preference is secondary to search relevance

    const newLastDoc = docs.length > 0 ? docs[docs.length - 1] : undefined;

    return {
      data,
      lastDoc: newLastDoc,
      hasMore,
      totalCount,
    };
  } catch (error) {
    console.error(`❌ Error executing search query for ${ledgerType}:`, error);
    throw new Error(
      `Failed to execute search query: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
}

/**
 * Get total count for search queries
 */
async function getSearchTotalCount(
  ledgerType: LedgerType,
  filters: Record<string, any>,
): Promise<number> {
  const collectionName = getCollectionName(ledgerType);
  const searchTerm = filters.search.toLowerCase().trim();

  try {
    const constraints: QueryConstraint[] = [];

    // Apply search constraints
    switch (ledgerType) {
      case LedgerType.TARGET:
        constraints.push(where("sellerName_lowercase", ">=", searchTerm));
        constraints.push(
          where("sellerName_lowercase", "<=", searchTerm + "\uf8ff"),
        );
        // Apply equality filters
        if (filters.cohort && filters.cohort !== "") {
          constraints.push(where("cohort", "==", filters.cohort));
        }
        if (filters.addedBy && filters.addedBy !== "") {
          constraints.push(where("addedBy", "==", filters.addedBy));
        }
        break;

      case LedgerType.MAILING:
        constraints.push(where("mailerId", ">=", searchTerm));
        constraints.push(where("mailerId", "<=", searchTerm + "\uf8ff"));
        // Apply equality filters
        if (filters.cohort && filters.cohort !== "") {
          constraints.push(where("cohort", "==", filters.cohort));
        }
        if (filters.addressingType && filters.addressingType !== "") {
          constraints.push(
            where("addressingType", "==", filters.addressingType),
          );
        }
        if (filters.addedBy && filters.addedBy !== "") {
          constraints.push(where("addedBy", "==", filters.addedBy));
        }
        if (
          filters.includeInMailingCostDashboard &&
          filters.includeInMailingCostDashboard !== ""
        ) {
          const includeValue = filters.includeInMailingCostDashboard === "Yes";
          constraints.push(
            where("includeInMailingCostDashboard", "==", includeValue),
          );
        }
        break;

      default:
        return 0; // Search not supported for this ledger type
    }

    const q = query(collection(db, collectionName), ...constraints);
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (error) {
    console.error(
      `❌ Error getting search total count for ${ledgerType}:`,
      error,
    );
    return 0;
  }
}
